import { useSyncExternalStore } from 'react';

import { isApiClientError, shortRequestId } from '@sm/api';

export type ToastTone = 'ok' | 'error' | 'info';
export interface ToastItem {
  id: number;
  message: string;
  tone: ToastTone;
  /** Puni request ID (kopira se); prikazuje se skraćeno kao „Šifra greške: ab12cd34“. */
  requestId?: string;
}

let items: ToastItem[] = [];
let seq = 0;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

export function dismissToast(id: number): void {
  items = items.filter((x) => x.id !== id);
  emit();
}

/** Prikazuje kratko obavještenje (3,2 s kao u dizajnu). */
export function toast(
  message: string,
  tone: ToastTone = 'ok',
  ms = 3200,
  requestId?: string,
): number {
  const id = ++seq;
  items = [...items.slice(-2), { id, message, tone, ...(requestId ? { requestId } : {}) }];
  emit();
  setTimeout(() => dismissToast(id), ms);
  return id;
}

/**
 * Toast greške: za mrežne, 5xx i neočekivane API greške dodaje šifru greške (request ID) sa kopiranjem i
 * ostaje duže na ekranu, da bi korisnik mogao da je pošalje podršci.
 */
export function errorToast(message: string, err: unknown): number {
  const rid = isApiClientError(err) && err.showsRequestId ? err.requestId : undefined;
  return toast(message, 'error', rid ? 9000 : 3200, rid);
}

type Translate = (key: string, opts?: Record<string, unknown>) => string;

/** Vraća limit paketa za ključ (`users`, `projectsPerMonth`...) iz keša pretplate; router ga registruje. */
type PlanLimitLookup = (key: string) => number | null | undefined;
let planLimitLookup: PlanLimitLookup | null = null;
export function setPlanLimitLookup(fn: PlanLimitLookup | null): void {
  planLimitLookup = fn;
}

/**
 * Poruka greške API-ja: `errors:<code>`; za `403 plan_limit` sa poljem (ključ limita) i poznatim limitom
 * `errors:planLimit.<polje>` („Paket dozvoljava najviše 3 korisnika“), inače opšta `errors:plan_limit`.
 */
export function apiErrorMessage(t: Translate, err: unknown): string {
  if (!isApiClientError(err)) return t('panel:toasts.genericError');
  if (err.has('plan_limit')) {
    const field = err.errors.find((e) => e.code === 'plan_limit')?.field;
    const limit = field ? planLimitLookup?.(field) : undefined;
    if (field && typeof limit === 'number') return t(`errors:planLimit.${field}`, { limit });
    return t('errors:plan_limit');
  }
  return t(err.i18nKey);
}

/** Poruka iz `errors:<code>` (ili opšta greška) + šifra greške. */
export function apiErrorToast(t: Translate, err: unknown): number {
  return errorToast(apiErrorMessage(t, err), err);
}

export { shortRequestId };

export function useToasts(): ToastItem[] {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => items,
    () => items,
  );
}
